import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { X } from 'lucide-react';
import { CircleCheck } from 'lucide-react-new';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPLIANCE_DATA, SECURITY_CONTROL_ITEMS } from './constants';

const SecurityAndControlsDialog = ({ open, onClose, selectedInfoItem }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));

  const renderTextWithLinks = useCallback(
    text => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = text.split(urlRegex);

      return parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <Link
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              sx={
                mediumDevice
                  ? {
                      color: 'var(--color-text)',
                      textDecoration: 'underline'
                    }
                  : {
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }
              }
            >
              {part}
            </Link>
          );
        }
        return part;
      });
    },
    [mediumDevice]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            backgroundColor: 'var(--neutral-2)',
            color: 'var(--color-text)',
            minWidth: '50vw',
            minHeight: 500
          }
        }
      }}
    >
      <DialogTitle
        style={{
          padding: theme.spacing(3),
          borderBottom: '1px solid var(--color-border)'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', gap: '10px' }}>
            <Typography variant="h4">{t(selectedInfoItem)}</Typography>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              zIndex: 3,
              cursor: 'pointer'
            }}
            onClick={onClose}
          >
            <X size={30} color="#70707B" />
          </div>
        </div>
      </DialogTitle>
      <DialogContent
        style={{
          padding: theme.spacing(3)
        }}
      >
        {selectedInfoItem === 'compliance' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2.5)
            }}
          >
            {COMPLIANCE_DATA.map(
              (reportInfo, index) => {
                return (
                          <Card
                        variant="outlined"
                        style={{
                          backgroundColor: 'var(--neutral-3)'
                        }}
                      >
                        <CardHeader
                          title={
                            <Box sx={{ display: 'flex', gap: '10px' }}>
                              <CircleCheck color="var(--accent-11)" size={22} />
                              <Typography variant="h4">
                                {reportInfo.subtitle}
                              </Typography>
                            </Box>
                          }
                        />
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: '10px',
                              paddingBottom: theme.spacing(3)
                            }}
                          >
                            <Typography style={{ fontWeight: 'bold' }}>Auditor</Typography>
                            <Typography>{reportInfo.auditor}</Typography>
                          </Box>
                          {
                            reportInfo.description.includes('risk@foundrydigital.com') ? 
                            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                              {
                                reportInfo.description.split(
                                  'risk@foundrydigital.com'
                                )[0]
                              }
                              <Link
                                href="mailto:risk@foundrydigital.com"
                                sx={{
                                  color: 'var(--accent-11)',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                risk@foundrydigital.com
                              </Link>
                              {
                                reportInfo.description.split(
                                  'risk@foundrydigital.com'
                                )[1]
                              }
                            </Typography> :
                          <Typography>
                            { reportInfo.description }
                          </Typography>
                          } 
                        </CardContent>
                      </Card>
                );
              }
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2.5)
            }}
          >
            {SECURITY_CONTROL_ITEMS[selectedInfoItem]?.fullDetails.map(
              (detail, index) => {
                return (
                  <Card
                    variant="outlined"
                    style={{ backgroundColor: 'var(--neutral-3)' }}
                    key={`${selectedInfoItem}-${detail.title}-${index}`}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '10px',
                          paddingBottom: theme.spacing(3)
                        }}
                      >
                        <CircleCheck color="var(--accent-11)" size={22} />
                        <Typography variant="h4">{detail.title}</Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        style={{
                          whiteSpace: 'pre-line',
                          wordWrap: 'break-word'
                        }}
                      >
                        {renderTextWithLinks(detail.description)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              }
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SecurityAndControlsDialog;
